import React from "react";

// Shared search-highlight helpers used by Bible, Catechism, and Prayers
// readers. Kept here so the regex-escape + <mark>-wrap behaviour stays
// identical across the three pages and we don't fork three copies.

export function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Strips diacritics so that a search for "oracion" still matches "oración".
// Returns the canonical-decomposition output minus combining marks. The
// length is NOT preserved (each combining mark is dropped), but we use
// this only for _matching_ — never for slicing the original string.
export function stripDiacritics(s) {
    return (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

// Word-level highlight (Bible / Catechism). Matches the literal query
// against the original text, accent-sensitive — keeps the simpler
// behaviour of the original Bible.jsx implementation.
//
// Implementation note: `re.test(p)` is safe here only because `split()`
// already isolated each capture group as its own array element. The /g
// flag's `lastIndex` would normally be problematic, but each call sees
// either a full match (returning true) or pure non-match text — never a
// partial overlap.
export function highlightText(text, query) {
    const q = (query || "").trim();
    if (!q) return text;
    const re = new RegExp(`(${escapeRegex(q)})`, "ig");
    const parts = text.split(re);
    return parts.map((p, i) =>
        re.test(p)
            ? <mark key={i} className="bg-sangre/15 text-stone900 rounded px-0.5">{p}</mark>
            : <React.Fragment key={i}>{p}</React.Fragment>,
    );
}

// Accent-insensitive highlighter (Prayers). Builds the regex on the
// normalised needle, matches against a normalised view of `text`, and
// emits slices from the ORIGINAL text so accents survive in the output.
//
// This relies on stripDiacritics being a 1-to-1 character mapping AFTER
// the NFD canonical decomposition: every accent becomes a separate
// combining codepoint that we drop, leaving the base letter index
// aligned with the original. True for all ES/EN catholic content. If
// pre-composed ligatures (e.g. "ﬁ") ever appear the indices would drift
// — not an issue today.
export function highlightTextDiacritic(text, query) {
    const q = (query || "").trim();
    if (!q) return text;
    const needle = stripDiacritics(q);
    if (!needle) return text;
    const re = new RegExp(escapeRegex(needle), "ig");
    const norm = stripDiacritics(text);
    const out = [];
    let last = 0;
    let m;
    while ((m = re.exec(norm)) !== null) {
        if (m.index > last) out.push(text.slice(last, m.index));
        out.push(
            <mark
                key={`${m.index}-${out.length}`}
                className="bg-sangre/15 text-stone900 rounded px-0.5"
            >
                {text.slice(m.index, m.index + m[0].length)}
            </mark>,
        );
        last = m.index + m[0].length;
        if (m[0].length === 0) re.lastIndex += 1;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}
